import type { OfficesBlockProps, Office } from '../types';
import { theme, sectionColors, cardGridClass, cssColor, cardTextColor } from '../theme';
import { PhoneWithLabel } from './AgentRoster';
import { formatPhone } from '../phone';

/** Compose a one-line address from the office's parts (skips blanks). */
function formatAddress(o: Office): string {
  const cityState = [o.city, o.state].filter(Boolean).join(', ');
  return [o.address, [cityState, o.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
}

/**
 * Pure office roster. Like the listings/agents blocks the renderer is "dumb": it
 * receives `offices` from the host (tenant-sites AND the builder preview both call
 * GET /mls/offices, which aggregates EVERY office ID on the tenant's MLS
 * integration) and NEVER fetches. Renders a graceful empty state rather than
 * throwing. Framework-agnostic so it transpiles in both Next.js and Vite.
 */
export function OfficesBlock({ props, offices }: { props: OfficesBlockProps; offices: Office[] }) {
  const { heading, limit, layout = 'grid', showAddress = true, showPhone = true } = props;
  const items = typeof limit === 'number' ? offices.slice(0, limit) : offices;
  const colors = sectionColors(props, { bg: 'transparent', text: theme.text });
  // The card's own colour (Card colour, else the site's Surface) and words readable on it.
  const cardBg = cssColor(props.cardBgColor);
  const cardLook = { backgroundColor: cardBg ?? theme.surface, color: cardTextColor(props, { cardBg, customCard: Boolean(cardBg) }) };

  if (items.length === 0) {
    return (
      <section className="px-6 py-12 text-center" style={{ ...colors, fontFamily: theme.bodyFont }}>
        <p className="opacity-60">No offices to show yet.</p>
      </section>
    );
  }

  const grid = cardGridClass({ columns: props.columns, layout });

  return (
    <section className="px-6 py-12" style={{ ...colors, fontFamily: theme.bodyFont }}>
      {heading ? (
        <h2 className="mx-auto mb-8 max-w-6xl text-2xl font-semibold" style={{ fontFamily: theme.headingFont }}>
          {heading}
        </h2>
      ) : null}
      <div className={`mx-auto grid max-w-6xl gap-6 ${grid}`}>
        {items.map((o) => {
          const address = formatAddress(o);
          return (
            <div
              key={o.key || o.mlsId || o.name || ''}
              className="blk-card rounded-lg border border-black/10 p-5"
              style={cardLook}
            >
              <div className="text-lg font-semibold" style={{ fontFamily: theme.headingFont }}>
                {o.name || 'Office'}
              </div>
              {showAddress && address ? (
                <div className="mt-1 text-sm opacity-70">{address}</div>
              ) : null}
              {showPhone && o.phone ? (
                <div className="mt-2 text-sm">
                  <PhoneWithLabel label={props.phoneLabel}>
                    <a
                      href={`tel:${o.phone.replace(/[^\d+]/g, '')}`}
                      className="blk-link font-medium underline-offset-2 hover:underline"
                      style={{ color: theme.primary }}
                    >
                      {formatPhone(o.phone)}
                    </a>
                  </PhoneWithLabel>
                </div>
              ) : null}
              {o.mlsId ? (
                <div className="mt-3 text-sm">
                  <a
                    href={`/offices/${encodeURIComponent(o.mlsId)}`}
                    className="blk-link font-medium underline-offset-2 hover:underline"
                    style={{ color: theme.primary }}
                  >
                    Agents &amp; listings →
                  </a>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}
